import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  TorneioPorIdData,
} from '../../../services/torneioService';
import {
  HISTORICO_POR_TORNEIO,
  HistoricoPorTorneioData,
  HistoricoPorTorneioVars,
  Historico,
} from '../../../services/historicoService';
import Load from '../../../components/Load';

interface TournamentHistoryTabProps {
  torneioId: string;
  torneio: TorneioPorIdData['torneioPorId'];
}

/**
 * Formata data e horário no padrão brasileiro.
 * @param value - String de data no formato ISO 8601
 * @returns String formatada como "DD/MM/YYYY HH:mm"
 */
const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('pt-BR') + ' ' +
  new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

/**
 * Componente da aba "Histórico" do gerenciamento de torneio.
 * 
 * Exibe todos os registros de histórico do torneio, ordenados por data descendente
 * (mais recente primeiro), mostrando data/horário e texto renderizado de cada item.
 */
const TournamentHistoryTab: React.FC<TournamentHistoryTabProps> = ({
  torneioId,
  torneio: _torneio,
}) => {
  const { data, loading, error } = useQuery<HistoricoPorTorneioData, HistoricoPorTorneioVars>(
    HISTORICO_POR_TORNEIO,
    {
      variables: { torneioId },
      skip: !torneioId,
      fetchPolicy: 'no-cache',
    }
  );

  // Ordena históricos por data descendente (mais recente primeiro)
  const historicosOrdenados = useMemo(() => {
    if (!data?.historicoPorTorneio?.nodes) return [];
    return [...data.historicoPorTorneio.nodes].sort((a, b) => 
      new Date(b.data).getTime() - new Date(a.data).getTime()
    );
  }, [data]);

  if (loading) {
    return (
      <div className="mt-4">
        <Load />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <div className="alert alert-danger" role="alert">
          Erro ao carregar histórico do torneio. Por favor, tente novamente.
        </div>
      </div>
    );
  }

  if (!data?.historicoPorTorneio || historicosOrdenados.length === 0) {
    return (
      <div className="mt-4">
        <div className="alert alert-info" role="alert">
          Não há registros de histórico disponíveis para este torneio.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <h4 className="fw-700 mb-4">Histórico do Torneio</h4>
          
          <div className="list-group">
            {historicosOrdenados.map((historico: Historico) => (
              <div
                key={historico.id}
                className="list-group-item border-start-0 border-end-0 border-top-0 border-bottom mb-2"
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <small className="text-muted fw-bold">
                    {formatDate(historico.data)}
                  </small>
                </div>
                <div className="text-break">
                  {historico.textoRenderizado || (
                    <span className="text-muted fst-italic">Sem descrição</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {data.historicoPorTorneio.totalCount > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Total de registros: {data.historicoPorTorneio.totalCount}
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentHistoryTab;

